import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import AdminListView from '@/components/AdminListView';
import { Plus, Search, Edit2, Trash2, Eye, BookOpen, Clock, Users, Save, Loader2, Upload, X, Play, Video, Tag, FolderPlus, UserCircle, Download } from 'lucide-react';
import { API } from '@/App';
import RichTextEditor from '@/components/RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CATEGORIES = [
  'Les Cours', 'Les Replays', 'Les Rapports', 'Les Publications Scientifiques',
  'Le Grand Prix de Contactologie', "Les Cahiers d'Ophtalmologie",
  'Articles de Formation', 'Cas Cliniques', 'Entretien', 'Keratocone',
  'Lentilles Hybrides', 'Lentilles Colorees', 'Lentilles Sclerales',
  'Lentilles Souples', 'LRPG', 'Myopie', 'Organisation',
  'Orthokeratologie', 'Pathologies', 'Pediatrie', 'Presbytie',
  'Resumes congres', 'Événements'
];
const LEVELS = [
  { value: 'beginner', label: 'Débutant' },
  { value: 'intermediate', label: 'Intermédiaire' },
  { value: 'advanced', label: 'Avancé' },
];
const SOURCES = [
  '', 'Les Cahiers d\'Ophtalmologie', 'Realites en Ophtalmologie', 'SFOALC', 'SFO'
];

const FormationsManagementPage = () => {
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', content: '', pdf_url: '',
    category: 'Les Cours', duration_minutes: '', level: 'intermediate',
    instructor: '', published: true, episodes: [], year: '', tags: '', source: '',
    author_id: '', author_name: '', author_avatar: '',
  });

  useEffect(() => { fetchFormations(); fetchCategories(); }, []);

  useEffect(() => {
    if (memberSearch.length < 2) { setMemberResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/users/search-for-bureau`, {
          params: { q: memberSearch },
          headers: { Authorization: `Bearer ${token}` }
        });
        setMemberResults(res.data || []);
      } catch { setMemberResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const selectAuthor = (member) => {
    setSelectedAuthor(member);
    setForm(p => ({
      ...p,
      author_id: member.id,
      author_name: `${member.first_name || ''} ${member.last_name || ''}`.trim(),
      author_avatar: member.profile_image || '',
      instructor: `${member.first_name || ''} ${member.last_name || ''}`.trim(),
    }));
    setMemberSearch('');
    setMemberResults([]);
    setShowMemberDropdown(false);
  };

  const clearAuthor = () => {
    setSelectedAuthor(null);
    setForm(p => ({ ...p, author_id: '', author_name: '', author_avatar: '', instructor: '' }));
  };

  useEffect(() => {
    const handleClick = () => setShowMemberDropdown(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const fetchFormations = async () => {
    try {
      const res = await axios.get(`${API}/formations`, { params: { published_only: false } });
      setFormations(res.data);
    } catch { toast.error('Erreur lors du chargement des formations'); }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/site-content/formation-categories`);
      if (res.data?.data?.categories?.length > 0) {
        setCategories(res.data.data.categories);
      }
    } catch { /* use defaults */ }
  };

  const saveCategories = async (cats) => {
    try {
      await axios.put(`${API}/site-content/formation-categories`, { data: { categories: cats } });
      setCategories(cats);
      toast.success('Catégories mises à jour');
    } catch { toast.error('Erreur'); }
  };

  const addCategory = () => {
    if (!newCategory.trim() || categories.includes(newCategory.trim())) return;
    const updated = [...categories, newCategory.trim()];
    saveCategories(updated);
    setNewCategory('');
  };

  const removeCategory = (cat) => {
    if (!window.confirm(`Supprimer la catégorie "${cat}" ?`)) return;
    saveCategories(categories.filter(c => c !== cat));
  };

  const resetForm = () => {
    setForm({ title: '', description: '', content: '', pdf_url: '', category: 'Les Cours', duration_minutes: '', level: 'intermediate', instructor: '', published: true, cover_image: '', episodes: [], year: '', tags: '', source: '', author_id: '', author_name: '', author_avatar: '' });
    setEditingFormation(null);
    setSelectedAuthor(null);
    setMemberSearch('');
  };

  const handleEdit = (f) => {
    setEditingFormation(f);
    setForm({
      title: f.title || '', description: f.description || '', content: f.content || '',
      pdf_url: f.pdf_url || '', category: f.category || 'Les Cours',
      duration_minutes: f.duration_minutes || '', level: f.level || 'intermediate',
      instructor: f.instructor || '', published: f.published !== false,
      cover_image: f.cover_image || '', episodes: f.episodes || [],
      year: f.year || '', tags: Array.isArray(f.tags) ? f.tags.join(', ') : (f.tags || ''),
      source: f.source || '',
      author_id: f.author_id || '', author_name: f.author_name || '', author_avatar: f.author_avatar || '',
    });
    if (f.author_id && f.author_name) {
      setSelectedAuthor({ id: f.author_id, first_name: f.author_name.split(' ')[0], last_name: f.author_name.split(' ').slice(1).join(' '), profile_image: f.author_avatar });
    } else {
      setSelectedAuthor(null);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    };
    try {
      if (editingFormation) {
        await axios.put(`${API}/formations/${editingFormation.id}`, payload);
        toast.success('Formation mise à jour');
      } else {
        await axios.post(`${API}/formations`, payload);
        toast.success('Formation créée');
      }
      setDialogOpen(false);
      resetForm();
      fetchFormations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer la formation "${title}" ?`)) return;
    try {
      await axios.delete(`${API}/formations/${id}`);
      toast.success('Formation supprimée');
      fetchFormations();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handlePdfUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, pdf_url: res.data.url }));
      toast.success('Document uploadé');
    } catch { toast.error("Erreur d'upload du document"); }
  };

  const filtered = formations.filter(f => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const matchSearch = f.title?.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        f.instructor?.toLowerCase().includes(q) ||
        f.author_name?.toLowerCase().includes(q);
      if (!matchSearch) return false;
    }
    if (categoryFilter !== 'all' && f.category !== categoryFilter) return false;
    if (yearFilter !== 'all' && String(f.year || '') !== String(yearFilter)) return false;
    if (authorFilter !== 'all') {
      const auth = f.author_name || f.instructor || '';
      if (auth !== authorFilter) return false;
    }
    if (levelFilter !== 'all' && f.level !== levelFilter) return false;
    if (sourceFilter !== 'all' && (f.source || '') !== sourceFilter) return false;
    if (statusFilter === 'published' && !f.published) return false;
    if (statusFilter === 'draft' && f.published) return false;
    return true;
  });

  // Build dynamic filter options from data
  const yearOptions = Array.from(new Set(formations.map(f => f.year).filter(Boolean))).sort((a, b) => b - a);
  const authorOptions = Array.from(new Set(formations.map(f => f.author_name || f.instructor).filter(Boolean))).sort();
  const sourceOptions = Array.from(new Set(formations.map(f => f.source).filter(Boolean))).sort();

  const resetFilters = () => {
    setSearchTerm(''); setCategoryFilter('all'); setYearFilter('all');
    setAuthorFilter('all'); setLevelFilter('all'); setSourceFilter('all'); setStatusFilter('all');
  };
  const activeFiltersCount = [
    searchTerm, categoryFilter !== 'all', yearFilter !== 'all', authorFilter !== 'all',
    levelFilter !== 'all', sourceFilter !== 'all', statusFilter !== 'all'
  ].filter(Boolean).length;

  const categoryCounts = categories.reduce((acc, cat) => {
    acc[cat] = formations.filter(f => f.category === cat).length;
    return acc;
  }, {});

  const levelLabel = (v) => LEVELS.find(l => l.value === v)?.label || v;

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto" data-testid="formations-management">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <BookOpen size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Contenus formation</h1>
              <p className="text-xs text-gray-500">Gérez les contenus de formation classés par catégories</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCategoryDialog(true)} data-testid="manage-categories-btn">
              <Tag size={14} className="mr-1" /> Catégories
            </Button>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="create-formation-btn">
              <Plus size={16} className="mr-2" /> Nouveau contenu
            </Button>
          </div>
        </div>

        {/* Filters bar */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Rechercher par titre, description, auteur..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-200 outline-none"
                data-testid="formations-search"
              />
            </div>

            <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white" data-testid="filter-year">
              <option value="all">Toutes années</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>

            <select value={authorFilter} onChange={e => setAuthorFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white max-w-[200px]" data-testid="filter-author">
              <option value="">Tous auteurs</option>
              <option value="all">Tous auteurs</option>
              {authorOptions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>

            <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white" data-testid="filter-level">
              <option value="all">Tous niveaux</option>
              {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>

            {sourceOptions.length > 0 && (
              <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white" data-testid="filter-source">
                <option value="all">Toutes sources</option>
                {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}

            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white" data-testid="filter-status">
              <option value="all">Tous statuts</option>
              <option value="published">Publié</option>
              <option value="draft">Brouillon</option>
            </select>

            {activeFiltersCount > 0 && (
              <button onClick={resetFilters}
                className="px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-colors flex items-center gap-1"
                data-testid="reset-filters">
                <X size={13} /> Tout effacer ({activeFiltersCount})
              </button>
            )}

            <span className="ml-auto text-xs text-gray-400 font-semibold whitespace-nowrap">
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${categoryFilter === 'all' ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid="filter-all-categories">
            Tous ({formations.length})
          </button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${categoryFilter === cat ? 'bg-violet-600 text-white' : 'bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
              data-testid={`filter-cat-${cat}`}>
              {cat} ({categoryCounts[cat] || 0})
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">Total</p>
            <p className="text-2xl font-bold text-gray-900">{formations.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">Durée totale</p>
            <p className="text-2xl font-bold text-gray-900">{formations.reduce((s, f) => s + (f.duration_minutes || 0), 0)} min</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">Publiées</p>
            <p className="text-2xl font-bold text-gray-900">{formations.filter(f => f.published).length}</p>
          </div>
        </div>

        {/* Table with AdminListView */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ height: 'calc(100vh - 500px)', minHeight: '350px' }}>
          <AdminListView
            data={filtered}
            loading={false}
            idKey="id"
            pageSize={20}
            emptyMessage="Aucune formation trouvée"
            columns={[
              {
                key: 'title', label: 'Titre', sortable: true,
                render: (_, f) => <p className="font-semibold text-sm text-gray-900 truncate max-w-[300px]">{f.title}</p>,
              },
              {
                key: 'author_name', label: 'Auteur', sortable: true,
                render: (_, f) => (f.author_name || f.instructor) ? (
                  <span className="text-xs text-gray-600 truncate">{f.author_name || f.instructor}</span>
                ) : <span className="text-xs text-gray-300">—</span>,
              },
              {
                key: 'category', label: 'Catégorie', sortable: true,
                render: (cat) => <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-700">{cat}</span>,
              },
              { key: 'year', label: 'Année', sortable: true, render: (v) => <span className="text-xs text-gray-600">{v || '–'}</span> },
              {
                key: 'published', label: 'Statut', sortable: true,
                render: (v) => <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${v ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{v ? 'Publié' : 'Brouillon'}</span>,
              },
              {
                key: 'actions', label: '', sortable: false,
                render: (_, f) => (
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={(e) => { e.stopPropagation(); handleEdit(f); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600"><Edit2 size={14} /></button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(f.id, f.title); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                ),
              },
            ]}
            onRowClick={(f) => handleEdit(f)}
            bulkActions={[
              {
                label: 'Exporter CSV',
                icon: Download,
                onClick: async (ids) => {
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(`${API}/admin/bulk/formations/export`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ ids, fields: ['title', 'author_name', 'category', 'year', 'source', 'published'] }),
                    });
                    if (res.ok) {
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `formations_${new Date().toISOString().slice(0,10)}.csv`;
                      a.click(); URL.revokeObjectURL(url);
                      toast.success(`${ids.length} formation(s) exportée(s)`);
                    }
                  } catch { toast.error("Erreur d'export"); }
                },
              },
              {
                label: 'Supprimer',
                icon: Trash2,
                variant: 'danger',
                onClick: async (ids) => {
                  if (!window.confirm(`Supprimer ${ids.length} formation(s) ?`)) return;
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(`${API}/admin/bulk/formations/delete`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ ids }),
                    });
                    if (res.ok) { toast.success('Formations supprimées'); fetchFormations(); }
                  } catch { toast.error('Erreur'); }
                },
              },
            ]}
          />
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingFormation ? 'Modifier la formation' : 'Nouvelle formation'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Cover Image */}
              <div>
                <Label className="mb-1.5 block">Image de couverture</Label>
                {form.cover_image ? (
                  <div className="relative rounded-xl overflow-hidden h-36 bg-gray-100">
                    <img src={form.cover_image.startsWith('/') ? `${API.replace('/api', '')}${form.cover_image}` : form.cover_image} alt="Cover" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm(p => ({...p, cover_image: ''}))} className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-lg hover:bg-black/80"><X size={14} /></button>
                  </div>
                ) : (
                  <label className="w-full h-28 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-[#35AFE4] hover:bg-blue-50/30 transition-all cursor-pointer" data-testid="formation-cover-upload">
                    <Upload size={20} className="text-gray-400" /><span className="text-xs text-gray-500">Importer une image</span>
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('file', file);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${API}/upload/cms-image`, fd, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } });
                        setForm(p => ({...p, cover_image: res.data.url}));
                        toast.success('Image importee');
                      } catch { toast.error("Erreur d'upload"); }
                    }} />
                  </label>
                )}
              </div>
              <div>
                <Label>Titre *</Label>
                <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required data-testid="formation-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Catégorie *</Label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="formation-category-select">
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Niveau</Label>
                  <select value={form.level} onChange={e => setForm(p => ({ ...p, level: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="formation-level-select">
                    {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <RichTextEditor value={form.description} onChange={val => setForm(p => ({ ...p, description: val }))} placeholder="Description de la formation..." height="100px" />
              </div>
              <div>
                <Label>Contenu *</Label>
                <RichTextEditor value={form.content} onChange={val => setForm(p => ({ ...p, content: val }))} placeholder="Contenu détaillé de la formation..." height="200px" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Auteur (membre)</Label>
                  <div className="relative" data-testid="author-selector">
                    {selectedAuthor ? (
                      <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                          {selectedAuthor.profile_image ? (
                            <img src={`${BACKEND_URL}${selectedAuthor.profile_image}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='block'); }} />
                          ) : null}
                          <span style={{ display: selectedAuthor.profile_image ? 'none' : 'block' }}>{(selectedAuthor.first_name?.[0] || '')}{(selectedAuthor.last_name?.[0] || '')}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-800 flex-1 truncate">{selectedAuthor.first_name} {selectedAuthor.last_name}</span>
                        <button type="button" onClick={clearAuthor} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors" data-testid="clear-author-btn">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="relative" onClick={e => e.stopPropagation()}>
                        <UserCircle size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          value={memberSearch}
                          onChange={e => { setMemberSearch(e.target.value); setShowMemberDropdown(true); }}
                          onFocus={() => memberSearch.length >= 2 && setShowMemberDropdown(true)}
                          placeholder="Rechercher un membre..."
                          className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#35AFE4] outline-none"
                          data-testid="author-search-input"
                        />
                      </div>
                    )}
                    {showMemberDropdown && memberResults.length > 0 && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="author-dropdown" onClick={e => e.stopPropagation()}>
                        {memberResults.map(m => (
                          <button key={m.id} type="button" onClick={() => selectAuthor(m)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#eaf6fc] transition-colors text-left"
                            data-testid={`author-option-${m.id}`}>
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 overflow-hidden">
                              {m.profile_image ? (
                                <img src={`${BACKEND_URL}${m.profile_image}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
                              ) : null}
                              <span style={{ display: m.profile_image ? 'none' : 'block' }}>{(m.first_name?.[0] || '')}{(m.last_name?.[0] || '')}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{m.first_name} {m.last_name}</p>
                              <p className="text-[10px] text-gray-400 truncate">{m.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Durée (minutes)</Label>
                  <Input type="number" value={form.duration_minutes} onChange={e => setForm(p => ({ ...p, duration_minutes: e.target.value }))} placeholder="60" data-testid="formation-duration-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Année</Label>
                  <Input value={form.year} onChange={e => setForm(p => ({ ...p, year: e.target.value }))} placeholder="2024" data-testid="formation-year-input" />
                </div>
                <div>
                  <Label>Source / Revue</Label>
                  <select value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="formation-source-select">
                    {SOURCES.map(s => <option key={s} value={s}>{s || '-- Aucune --'}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Tags / Mots-clés</Label>
                  <Input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="Myopie, Pédiatrie" data-testid="formation-tags-input" />
                </div>
              </div>
              <div>
                <Label>Document PDF</Label>
                <div className="flex items-center gap-3">
                  {form.pdf_url && <a href={form.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-[#35AFE4] hover:underline truncate max-w-xs">{form.pdf_url}</a>}
                  <label className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium cursor-pointer transition-colors">
                    <Upload size={12} /> Uploader un PDF
                    <input type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) handlePdfUpload(e.target.files[0]); }} />
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} id="pub-check" className="rounded" />
                <label htmlFor="pub-check" className="text-sm text-gray-700">Publier immédiatement</label>
              </div>

              {/* Episodes (for Replays only) */}
              {form.category === 'Les Replays' && (
                <div className="border border-gray-200 rounded-xl p-4 space-y-3" data-testid="episodes-section">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Video size={16} className="text-[#35AFE4]" />
                      <Label className="font-bold">Épisodes YouTube</Label>
                    </div>
                    <button type="button" onClick={() => setForm(p => ({
                      ...p, episodes: [...(p.episodes || []), { id: crypto.randomUUID(), order: (p.episodes?.length || 0) + 1, title: '', youtube_url: '', youtube_id: '', duration: '' }]
                    }))} className="flex items-center gap-1 px-3 py-1.5 bg-[#35AFE4] text-white rounded-lg text-xs font-medium hover:bg-[#2d7d9e]" data-testid="add-episode-btn">
                      <Plus size={12} /> Ajouter
                    </button>
                  </div>
                  {(form.episodes || []).map((ep, i) => (
                    <div key={ep.id || i} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">{i + 1}</div>
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <Input placeholder="Titre de l'épisode" value={ep.title} onChange={e => {
                          const eps = [...form.episodes]; eps[i] = { ...eps[i], title: e.target.value }; setForm(p => ({ ...p, episodes: eps }));
                        }} className="col-span-2 text-xs" data-testid={`episode-title-${i}`} />
                        <Input placeholder="Durée (ex: 25 min)" value={ep.duration} onChange={e => {
                          const eps = [...form.episodes]; eps[i] = { ...eps[i], duration: e.target.value }; setForm(p => ({ ...p, episodes: eps }));
                        }} className="text-xs" />
                        <Input placeholder="URL YouTube (ex: https://youtube.com/watch?v=xxx)" value={ep.youtube_url} onChange={e => {
                          const url = e.target.value;
                          const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
                          const ytId = match ? match[1] : '';
                          const eps = [...form.episodes]; eps[i] = { ...eps[i], youtube_url: url, youtube_id: ytId }; setForm(p => ({ ...p, episodes: eps }));
                        }} className="col-span-2 text-xs" data-testid={`episode-url-${i}`} />
                        <button type="button" onClick={() => {
                          const eps = form.episodes.filter((_, idx) => idx !== i); setForm(p => ({ ...p, episodes: eps }));
                        }} className="flex items-center justify-center text-red-400 hover:text-red-600">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!form.episodes || form.episodes.length === 0) && (
                    <p className="text-center text-xs text-gray-400 py-4">Aucun épisode. Cliquez sur "Ajouter" pour créer des épisodes YouTube.</p>
                  )}
                </div>
              )}

              <Button type="submit" disabled={saving} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="submit-formation-btn">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                {editingFormation ? 'Mettre à jour' : 'Créer la formation'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Category management dialog */}
        <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag size={18} className="text-violet-600" /> Gérer les catégories
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="flex gap-2">
                <Input value={newCategory} onChange={e => setNewCategory(e.target.value)}
                  placeholder="Nouvelle catégorie..." onKeyDown={e => { if (e.key === 'Enter') addCategory(); }}
                  data-testid="new-category-input" />
                <Button onClick={addCategory} size="sm" data-testid="add-category-btn">
                  <FolderPlus size={14} className="mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {categories.map((cat, i) => (
                  <div key={cat} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg group" data-testid={`category-item-${i}`}>
                    <span className="text-sm font-medium text-gray-700">{cat}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{categoryCounts[cat] || 0} contenus</span>
                      <button onClick={() => removeCategory(cat)}
                        className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-category-${i}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default FormationsManagementPage;
